import { c as _c } from "react/compiler-runtime";
import { useParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { type GraphQLDepartureChild, GraphQLDepartureExitType } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { findDepartureCommitment, getSponsorYears } from '@sola/shared-utils/supporters/departures.ts';
import { useDepartureSummary, useSponsorshipsAny } from '@sola/supporter-sdk/hooks/commitments.ts';
interface DepartureSpotlightCardData {
  childGlobalId: string;
  childImageUrl?: string | null;
  childName: string;
  gender?: string | null;
  sponsorYears?: number;
  titleKey: string;
}
type DepartureSummaryLoadingState = {
  isFetching: boolean;
  isLoading: boolean;
  isPending: boolean;
};
export const isDepartureSummaryLoading = ({
  isFetching,
  isLoading,
  isPending
}: DepartureSummaryLoadingState): boolean => isLoading || isFetching || isPending;
export const getChildName = (child: GraphQLDepartureChild): string => (child.displayName ?? '').trim();
export const hasMultipleSponsorships = (sponsorshipCount: number): boolean => sponsorshipCount > 1;
export const useDepartureSpotlightCard = () => {
  const $ = _c(19);
  const params = useParams();
  const globalId = params?.globalId;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["my"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const departureSummary = useDepartureSummary();
  const sponsorshipsQuery = useSponsorshipsAny();
  let departure;
  let isLoading;
  let t1;
  if ($[1] !== departureSummary || $[2] !== globalId || $[3] !== sponsorshipsQuery) {
    departure = departureSummary.departureSummary?.departures.find(t2 => {
      const {
        departedChild
      } = t2;
      return departedChild.globalId === globalId;
    });
    let t3;
    if ($[7] !== departureSummary || $[8] !== sponsorshipsQuery) {
      t3 = isDepartureSummaryLoading(departureSummary) || isDepartureSummaryLoading(sponsorshipsQuery);
      $[7] = departureSummary;
      $[8] = sponsorshipsQuery;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    isLoading = t3;
    const titleKey = departure?.exitType === GraphQLDepartureExitType.Unplanned ? "my:sponsorshipRelationship.departures.spotlight.departure.unplannedTitle" : "my:sponsorshipRelationship.departures.spotlight.departure.title";
    const commitment = departure ? findDepartureCommitment(sponsorshipsQuery.sponsorships, departure.departedChild) : undefined;
    const sponsorYears = departure ? getSponsorYears(departure, sponsorshipsQuery.sponsorships) : undefined;
    t1 = departure ? {
      childGlobalId: departure.departedChild.globalId,
      childImageUrl: departure.departedChild.imageUrl,
      childName: getChildName(departure.departedChild),
      gender: commitment?.participant?.gender,
      ...(sponsorYears ? {
        sponsorYears
      } : {}),
      titleKey
    } : undefined;
    $[1] = departureSummary;
    $[2] = globalId;
    $[3] = sponsorshipsQuery;
    $[4] = departure;
    $[5] = isLoading;
    $[6] = t1;
  } else {
    departure = $[4];
    isLoading = $[5];
    t1 = $[6];
  }
  const cardData = t1;
  const t2 = !!globalId;
  let t3;
  if ($[10] !== sponsorshipsQuery.sponsorships.length) {
    t3 = hasMultipleSponsorships(sponsorshipsQuery.sponsorships.length);
    $[10] = sponsorshipsQuery.sponsorships.length;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const t4 = departureSummary.isError || sponsorshipsQuery.isError || !isLoading && !departure;
  let t5;
  if ($[12] !== cardData || $[13] !== isLoading || $[14] !== t || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = {
      t,
      hasGlobalId: t2,
      hasMultipleSponsorships: t3,
      isLoading,
      hasError: t4,
      cardData
    };
    $[12] = cardData;
    $[13] = isLoading;
    $[14] = t;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
};